import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Brain, Calendar, Network, Plus, Search, Filter, Tag, Clock } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

export default function MemoryPage() {
  const { presenceId } = useParams();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('episodic');
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchMemories();
  }, [presenceId, activeTab]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/memory/list/${presenceId}`, {
        params: { 
          type: activeTab === 'episodic' ? 'episodic' : activeTab === 'semantic' ? 'semantic' : null 
        }
      });
      setMemories(res.data.memories || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'episodic', label: 'Episodic', icon: Calendar },
    { id: 'semantic', label: 'Semantic', icon: Brain },
    { id: 'graph', label: 'Graph', icon: Network },
  ];

  const filteredMemories = memories.filter(mem => {
    const matchesSearch = !searchQuery || mem.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || mem.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800 px-4 lg:px-6 py-3 lg:py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <div className="text-[10px] lg:text-xs uppercase tracking-[0.2em] text-cyan-400/80 mb-1">
              🧠 {t('presence.memory')}
            </div>
            <h1 className="text-lg lg:text-xl font-bold">{t('memory.knowledgeBase')}</h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-3 lg:px-4 py-2 text-sm bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">{t('memory.addMemory')}</span>
            <span className="sm:hidden">+</span>
          </button>
        </div>
        
        {/* Info Card */}
        <div className="bg-cyan-400/5 border border-cyan-400/20 rounded-lg p-3 lg:p-4 mb-4">
          <p className="text-xs lg:text-sm text-slate-300 mb-2">
            <strong className="text-cyan-400">{t('memory.whatIsThis')}</strong> {t('memory.explanation')}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-cyan-400" />
              <span><strong>{t('memory.episodic')}:</strong> {t('memory.episodicDesc')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Brain size={14} className="text-purple-400" />
              <span><strong>{t('memory.semantic')}:</strong> {t('memory.semanticDesc')}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={[
                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-cyan-400/10 text-cyan-50 border border-cyan-400/40'
                  : 'text-slate-400 hover:text-cyan-100 hover:bg-slate-900/50'
              ].join(' ')}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'episodic' && <EpisodicTab memories={filteredMemories} loading={loading} onRefresh={fetchMemories} />}
        {activeTab === 'semantic' && <SemanticTab memories={filteredMemories} loading={loading} onRefresh={fetchMemories} />}
        {activeTab === 'graph' && <GraphTab presenceId={presenceId} />}
      </main>
      
      {/* Add Memory Modal */}
      {showAddModal && (
        <AddMemoryModal
          presenceId={presenceId}
          memoryType={activeTab === 'graph' ? 'episodic' : activeTab}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchMemories();
          }}
        />
      )}
    </div>
  );
}

// Tab 1: Episodic Memories
function EpisodicTab({ memories, loading, onRefresh }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredMemories = memories.filter(m => 
    !searchQuery || m.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 bg-slate-900/50 border border-slate-800 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Search & Filters */}
      <div className="mb-6 flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search memories..."
            className="w-full pl-10 pr-4 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-100 text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400"
          />
        </div>
        <select className="px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-slate-300 text-sm">
          <option>All Categories</option>
          <option>Personal</option>
          <option>Career</option>
          <option>Family</option>
        </select>
      </div>

      {/* Memory List */}
      {filteredMemories.length === 0 ? (
        <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-12 text-center">
          <Calendar className="mx-auto mb-3 text-slate-600" size={48} />
          <p className="text-slate-400">No episodic memories yet</p>
          <p className="text-xs text-slate-500 mt-2">Add important moments and experiences</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMemories.map((memory, idx) => (
            <MemoryCard key={memory.id || idx} memory={memory} onRefresh={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
}

// Memory Card Component
function MemoryCard({ memory, onRefresh }) {
  return (
    <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-4 hover:border-slate-700 transition group">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            {memory.category && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 rounded text-[10px] font-medium uppercase tracking-wide">
                <Tag size={10} />
                {memory.category}
              </span>
            )}
            {memory.createdAt && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Clock size={12} />
                {new Date(memory.createdAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-200">{memory.content}</p>
        </div>
        
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
          <button className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-slate-800 rounded transition">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded transition">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Tab 2: Semantic Knowledge
function SemanticTab({ memories, loading, onRefresh }) {
  const categories = [...new Set(memories.map(m => m.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-slate-900/50 border border-slate-800 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {/* Topics */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Topics & Concepts</h3>
        <div className="flex flex-wrap gap-2">
          {categories.length === 0 ? (
            <p className="text-sm text-slate-500">No topics yet</p>
          ) : (
            categories.map(cat => (
              <span
                key={cat}
                className="px-3 py-1 bg-purple-400/10 border border-purple-400/40 text-purple-200 rounded-full text-sm"
              >
                {cat}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Knowledge List */}
      {memories.length === 0 ? (
        <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-12 text-center">
          <Brain className="mx-auto mb-3 text-slate-600" size={48} />
          <p className="text-slate-400">No semantic knowledge yet</p>
          <p className="text-xs text-slate-500 mt-2">Add facts, preferences, and concepts</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map(cat => {
            const catMemories = memories.filter(m => m.category === cat);
            return (
              <div key={cat} className="border border-slate-800 rounded-xl bg-slate-900/50 p-4">
                <h4 className="text-sm font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-purple-400" />
                  {cat} ({catMemories.length})
                </h4>
                <ul className="space-y-2">
                  {catMemories.map((mem, idx) => (
                    <li key={idx} className="text-sm text-slate-300 pl-4 border-l-2 border-slate-800">
                      {mem.content}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Tab 3: Memory Graph
function GraphTab({ presenceId }) {
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGraph();
  }, [presenceId]);

  const fetchGraph = async () => {
    try {
      const res = await api.get(`/dashboard/memory-graph/${presenceId}`);
      setGraphData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading graph...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h3 className="text-lg font-bold mb-2">Memory Network Visualization</h3>
        <p className="text-sm text-slate-400">
          Explore connections between memories
        </p>
      </div>

      {/* Graph Placeholder */}
      <div className="border border-slate-800 rounded-xl bg-slate-900/50 p-12 text-center">
        <Network className="mx-auto mb-3 text-slate-600" size={64} />
        <p className="text-slate-400 mb-2">Graph visualization coming soon</p>
        <p className="text-xs text-slate-500">
          Node size = connection count • Color = category
        </p>
        
        {/* Simple stats for now */}
        {graphData && (
          <div className="mt-6 grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-cyan-300">{graphData.nodes?.length || 0}</div>
              <div className="text-xs text-slate-500">Nodes</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-cyan-300">{graphData.connections?.length || 0}</div>
              <div className="text-xs text-slate-500">Connections</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <div className="text-2xl font-bold text-cyan-300">
                {[...new Set(graphData.nodes?.map(n => n.category))].length || 0}
              </div>
              <div className="text-xs text-slate-500">Categories</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Add Memory Modal Component
function AddMemoryModal({ presenceId, memoryType, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    tags: '',
    // Episodic specific
    occurredAt: new Date().toISOString().split('T')[0],
    emotionalTone: 'neutral',
    relatedPeople: '',
    // Semantic specific
    topic: 'general'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description) {
      toast.error(t('memory.fillRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        presenceId,
        title: formData.title,
        description: formData.description,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (memoryType === 'episodic') {
        payload.occurredAt = formData.occurredAt;
        payload.emotionalTone = formData.emotionalTone;
        payload.relatedPeople = formData.relatedPeople.split(',').map(p => p.trim()).filter(Boolean);
      } else {
        payload.topic = formData.topic;
      }

      await api.post('/memory/create', {
        ...payload,
        memoryType
      });

      toast.success(t('memory.memoryAdded'));
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(t('memory.addError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-50">
              {t('memory.addMemory')} - {memoryType === 'episodic' ? t('memory.episodic') : t('memory.semantic')}
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              {memoryType === 'episodic' ? t('memory.episodicHelp') : t('memory.semanticHelp')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 transition"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('memory.title')} *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
              placeholder={memoryType === 'episodic' ? t('memory.episodicTitlePlaceholder') : t('memory.semanticTitlePlaceholder')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('memory.description')} *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition resize-none"
              placeholder={t('memory.descriptionPlaceholder')}
            />
          </div>

          {/* Episodic Specific Fields */}
          {memoryType === 'episodic' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('memory.date')}
                  </label>
                  <input
                    type="date"
                    value={formData.occurredAt}
                    onChange={(e) => setFormData({ ...formData, occurredAt: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {t('memory.emotion')}
                  </label>
                  <select
                    value={formData.emotionalTone}
                    onChange={(e) => setFormData({ ...formData, emotionalTone: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
                  >
                    <option value="positive">{t('memory.positive')}</option>
                    <option value="neutral">{t('memory.neutral')}</option>
                    <option value="negative">{t('memory.negative')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('memory.relatedPeople')}
                </label>
                <input
                  type="text"
                  value={formData.relatedPeople}
                  onChange={(e) => setFormData({ ...formData, relatedPeople: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
                  placeholder={t('memory.relatedPeoplePlaceholder')}
                />
              </div>
            </>
          )}

          {/* Semantic Specific Fields */}
          {memoryType === 'semantic' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                {t('memory.topic')}
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
                placeholder={t('memory.topicPlaceholder')}
              />
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {t('memory.tags')}
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
              placeholder={t('memory.tagsPlaceholder')}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 text-sm bg-cyan-400 text-slate-900 font-medium rounded-lg hover:bg-cyan-300 transition disabled:opacity-50"
            >
              {saving ? t('memory.saving') : t('memory.save')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 transition"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

